import { Phone, MapPin, HardHat, Home, Hammer, ClipboardList } from 'lucide-react';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import PackageCard from '../components/PackageCard';
import FaqAccordion from '../components/FaqAccordion';
import { IMAGES, PACKAGES } from '../data/content';

const NEEDS = [
  {
    icon: ClipboardList,
    title: 'Win the quote before you visit',
    text: 'Extensions and renovations are decided after careful research. A professional site with real project photos means you arrive at the quoting stage already trusted — not competing on price alone.',
  },
  {
    icon: MapPin,
    title: 'You show up for “builder near me”',
    text: 'A complete Google Business Profile with the right categories, service areas and photos is what puts a local builder on the map. We set it up properly and guide you through Google’s verification.',
  },
  {
    icon: Phone,
    title: 'Serious enquiries reach you directly',
    text: 'Click-to-call, WhatsApp and a simple enquiry form on every page — so a homeowner planning a £40,000 extension can contact you in seconds, straight from their phone.',
  },
];

const WORK_TYPES = [
  {
    icon: Home,
    title: 'Extensions',
    text: 'The biggest job most homeowners ever commission. Before-and-after galleries and genuine reviews do more selling than any advert.',
  },
  {
    icon: Hammer,
    title: 'Renovations and conversions',
    text: 'Lofts, garages, full refurbishments — customers planning months ahead compare builders carefully online long before they ring anyone.',
  },
  {
    icon: HardHat,
    title: 'General building work',
    text: 'Repointing, repairs, garden walls and everything between — a clear services list means the phone rings for the work you actually want.',
  },
];

const BUILDER_FAQS = [
  {
    q: 'My work speaks for itself — why do I need a website?',
    a: 'Because customers can’t see your work until you show it to them. Right now your best projects live in your camera roll and your reputation lives in other people’s conversations. A website puts both where a homeowner planning an extension will actually look — before they ever shortlist builders to quote.',
  },
  {
    q: 'Can the site show big projects properly, not just thumbnails?',
    a: 'Yes. The Look Professional package includes a gallery page, and Built to Grow expands it into a fuller portfolio with room to tell the story of each project — the brief, the work, the finish. Phone photos work well; we’ll show you how to take useful ones.',
  },
  {
    q: 'I only want bigger jobs, not small repairs. Can the site filter that?',
    a: 'To a sensible degree, yes. Your services and enquiry form are written around the work you want — extensions, renovations and larger projects — so enquiries skew that way. It won’t stop every small-job call, but it sets expectations before the phone rings.',
  },
  {
    q: 'I’m on site all day. How much of my time does this take?',
    a: 'About an hour in total, spread across the build: one plain-English questionnaire, some project photos from your phone, and a look at the finished site before launch. Everything else is handled for you.',
  },
];

export default function BuildersPage() {
  return (
    <>
      <Seo
        title="Websites for Builders — Win Extensions & Renovations | Build My Trade"
        description="Professional websites, Google Business Profile setup and social pages built specifically for UK builders. Fixed packages from £249, plain English, everything handled."
        path="/websites-for-builders"
        image={IMAGES.carpentry.url}
      />

      {/* Hero */}
      <section className="bg-charcoal bg-blueprint" data-testid="builders-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">
              Websites for builders
            </p>
            <h1 className="mt-5 text-balance font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-offwhite sm:text-5xl lg:text-6xl">
              Win the extension before you’ve <span className="text-lime">priced it.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-offwhite/70 sm:text-lg">
              Build My Trade sets up everything a builder needs to be trusted with bigger projects: a professional
              website with a proper project gallery, a complete Google Business Profile and branded social pages —
              for one fixed, published price.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <ArrowLink to="/pricing" variant="lime" testid="builders-cta-packages" className="px-7 py-3.5">
                See Fixed-Price Packages
              </ArrowLink>
              <ArrowLink to="/contact" variant="outline-light" testid="builders-cta-quote" className="px-7 py-3.5">
                Get a Free Quote
              </ArrowLink>
            </div>
            <p className="mt-7 text-sm text-offwhite/50">
              Straightforward packages. Clear prices. No marketing jargon.
            </p>
          </div>
          <Reveal>
            <img
              src={IMAGES.carpentry.url}
              alt={IMAGES.carpentry.alt}
              loading="eager"
              className="aspect-[4/3] w-full rounded-2xl border border-charcoal-line object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* What a builder's online presence needs */}
      <section className="bg-offwhite" data-testid="builders-needs">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Built around how bigger jobs are won
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              What a builder’s website has to do
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {NEEDS.map((n, i) => (
              <Reveal key={i} delay={0.08 * i} className="h-full">
                <div className="h-full rounded-2xl border border-hairline bg-white p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-charcoal">
                    <n.icon className="h-5 w-5 text-lime" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-charcoal">{n.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stonebrand">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Work types */}
      <section className="border-y border-hairline bg-white" data-testid="builders-work-types">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                  The projects worth quoting for
                </p>
                <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
                  Set up to attract the projects you want more of
                </h2>
                <p className="mt-5 text-base leading-relaxed text-stonebrand">
                  Building work isn’t one kind of job. Your website speaks to each customer differently — the family
                  planning an extension for next spring, the landlord with a renovation, the homeowner with a list of
                  repairs.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="mt-8">
                <img
                  src={IMAGES.workshop.url}
                  alt={IMAGES.workshop.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-2xl object-cover"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {WORK_TYPES.map((w, i) => (
                  <Reveal key={i} delay={0.08 * i}>
                    <li className="flex gap-5 rounded-2xl border border-hairline bg-offwhite-light p-7">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-lime">
                        <w.icon className="h-5 w-5 text-charcoal" aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="font-heading text-lg font-bold tracking-tight text-charcoal">{w.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-stonebrand">{w.text}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-charcoal" data-testid="builders-packages">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">Fixed, published prices</p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
              Packages that fit a building business
            </h2>
            <p className="mt-5 text-base leading-relaxed text-offwhite/65">
              The same straightforward packages we offer every trade — one-off payments, everything listed, nothing
              hidden. Builders quoting for larger projects tend to choose Built to Grow: up to ten pages, an expanded
              portfolio and your branding sorted at the same time.
            </p>
          </Reveal>
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {PACKAGES.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} delay={0.08 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* Builder FAQs */}
      <section className="bg-offwhite" data-testid="builders-faqs">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Questions builders ask us
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              Straight answers for busy builders
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <FaqAccordion items={BUILDER_FAQS} testid="builders-faq" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal-deep bg-blueprint" data-testid="builders-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                Ready to be the builder customers <span className="text-lime">shortlist first?</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                Tell us about your building business and the kind of projects you want more of — we’ll recommend the
                right starting point, in plain English, with no obligation.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" variant="lime" testid="builders-cta-final" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
